import { KeysOfUnion } from "@costory/types"
import {
  ActionIcon,
  Blockquote,
  Button,
  Checkbox,
  Group,
  InputWrapper,
  List,
  ListItem,
  Loader,
  Modal,
  Radio,
  ScrollArea,
  Select,
  Space,
  Stack,
  Stepper,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { useForm, UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconCloudX,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { z } from "zod"

import {
  awsImportParametersSchema,
  azureImportParametersSchema,
  BigQueryBillingDatasourceFormData,
  bigQueryColumnMappingSchema,
  bigQueryImportParametersSchema,
  BillingDatasourceFormData,
  BillingDatasourcesRequests,
  BillingDatasourcesResponses,
  ConfluentConfigurationFormData,
  confluentConfigurationSchema,
  datadogMetadataSchema,
  aivenMetadataSchema,
  gcpImportParametersSchema,
  BQErrorCode,
} from "@costory/types/endpoints/billingDatasources"
import { MAPPING_BQ_TYPES } from "@costory/types/endpoints/shared"
import { BillingDatasourceType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"

import { BigQueryErrorDisplay } from "@costory/front/components/BigQueryErrorDisplay"
import {
  AwsSvgComponent,
  AzureSvgComponent,
  BigquerySvgComponent,
  ConfluentSvgComponent,
  DatadogSvgComponent,
  GcpSvgComponent,
  AivenSvgComponent,
} from "@costory/front/components/DatasourceImages"
import { useUser } from "@costory/front/components/auth/UserContext"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { queryClient } from "@costory/front/lib/queryClient"
import { runtimeConfig } from "@costory/front/lib/runtimeConfig"
import {
  useBillingDatasourcesListQuery,
  useCreateBillingDatasourceMutation,
  useDeleteBillingDatasourceMutation,
  useGetBigQueryColumnsQuery,
  useValidateImportParamtersQuery,
} from "@costory/front/queries/billingDatasources"
import { PropsWithData } from "@costory/front/utils/propsWithData"
import { getDefaultTableOptions } from "@costory/front/utils/table"

import { findBestColumnMatch } from "./utils"

const _BillingDatasourcesPage = ({
  data: datasources,
}: PropsWithData<BillingDatasourcesResponses.GetAllBillingDatasources>) => {
  const [
    isOpen,
    { open: openImportDatasourceModal, close: closeImportDatasourceModal },
  ] = useDisclosure(false)

  const { mutateAsync: deleteDatasource, isPending: isDeleting } =
    useDeleteBillingDatasourceMutation()
  const openDeleteModal = useCallback(
    (toDelete: BillingDatasourcesResponses.BillingDatasource) =>
      modals.openConfirmModal({
        title: "Please confirm you want to delete " + toDelete.name,
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => deleteDatasource(toDelete!.id),
      }),
    [deleteDatasource],
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const isDetailsModalOpen = searchParams.get("details") !== null
  const openDetailsModal = (id: string) => {
    setSearchParams({ details: id })
  }
  const closeDetailsModal = () => {
    setSearchParams({})
  }

  const columns = useMemo(() => {
    const columnHelper =
      createMRTColumnHelper<BillingDatasourcesResponses.BillingDatasource>()
    return [
      columnHelper.accessor("type", {
        header: "Type",
        enableColumnActions: false,
        enableSorting: false,
        Header: () => null,
        Cell: ({ cell }) => {
          const datasource = availableDatasources.find(
            ({ value }) => value === cell.getValue(),
          )

          if (!datasource) {
            return null
          }

          const { icon: IconComponent, label } = datasource

          return (
            <Group align="center">
              <Tooltip label={label}>
                <ThemeIcon size={50}>{IconComponent}</ThemeIcon>
              </Tooltip>
            </Group>
          )
        },
      }),
      columnHelper.accessor("name", {
        header: "Name",
        filterVariant: "select",
      }),
      columnHelper.accessor("updatedAt", {
        header: "Last Update",
        Cell: ({ cell }) => dayjs(cell.getValue()).format(DEFAULT_DATE_FORMAT),
      }),
    ]
  }, [])

  const table =
    useMantineReactTable<BillingDatasourcesResponses.BillingDatasource>({
      columns,
      data: datasources,
      enableRowActions: true,
      positionActionsColumn: "last",
      renderRowActions: ({ row }) => (
        <ActionIcon
          onClick={() => {
            openDeleteModal(row.original)
          }}
          loading={isDeleting}
          data-intercom-target="costory-tour-datasource-billing-delete-button"
        >
          <IconTrash />
        </ActionIcon>
      ),
      ...getDefaultTableOptions(),
      mantineTableBodyRowProps: ({ row: { original: datasource } }) => ({
        onDoubleClick: () => {
          openDetailsModal(datasource.id)
        },
      }),
    })

  return (
    <Stack>
      <Group justify="flex-end">
        <Button
          onClick={openImportDatasourceModal}
          leftSection={<IconPlus />}
          data-intercom-target="costory-tour-datasource-billing-add-button"
        >
          Import new billing datasource
        </Button>
      </Group>

      <MantineReactTable table={table} />
      <Modal
        opened={isOpen}
        onClose={closeImportDatasourceModal}
        size="70vw"
        classNames={{ content: "custom-modal-content" }}
        title={<Title order={2}>Import new billing datasource</Title>}
      >
        <Modal.Body h="70vh">
          <AddBillingDataSourceFunnel onFinish={closeImportDatasourceModal} />
        </Modal.Body>
      </Modal>
      <Modal
        opened={isDetailsModalOpen}
        onClose={closeDetailsModal}
        size="70vw"
      >
        <BillingDatasourceDetails
          billingDatasource={
            datasources.find(({ id }) => id === searchParams.get("details"))!
          }
        />
      </Modal>
    </Stack>
  )
}
const BillingDatasourceDetails = ({
  billingDatasource,
}: {
  billingDatasource: BillingDatasourcesResponses.BillingDatasource
}) => {
  if (!billingDatasource) {
    return null
  }

  const IconComponent = availableDatasources.find(
    ({ value }) => value === billingDatasource.type,
  )!.icon

  return (
    <Stack>
      <Group>
        {IconComponent}
        <Stack gap={0}>
          <Title>Billing datasource {billingDatasource.name}</Title>
          <Group>
            <Text fw="bold">Owner: {billingDatasource.owner}</Text>
            <Text>
              Last update:{" "}
              {dayjs(billingDatasource.updatedAt).format("DD/MM/YYYY")}
            </Text>
          </Group>
        </Stack>
      </Group>
      <Stack align="stretch" w="40%">
        {questions[billingDatasource.type]?.map(({ key, label, type }) => {
          return type === "text" || type === "password" ? (
            <InputWrapper label={label} key={key}>
              <TextInput
                // @ts-expect-error metadata is a key of the datasource
                value={billingDatasource.metadata[key]}
                placeholder={label}
                readOnly
                type={type}
              />
            </InputWrapper>
          ) : type === "checkbox" ? (
            <Group>
              <Text key={key}>{label}:</Text>
              {/* @ts-expect-error metadata is a key of the datasource */}
              <Text>{billingDatasource.metadata[key] ? "Yes" : "No"}</Text>
            </Group>
          ) : (
            <InputWrapper label={label} key={key}>
              <DateInput
                // @ts-expect-error metadata is a key of the datasource
                value={billingDatasource.metadata[key]}
                readOnly
              />
            </InputWrapper>
          )
        })}
      </Stack>
    </Stack>
  )
}

export const BillingDatasourcesPage = () => {
  const datasourceQuery = useBillingDatasourcesListQuery()

  return (
    <QueryWrapper query={datasourceQuery} allowEmptyArray>
      {_BillingDatasourcesPage}
    </QueryWrapper>
  )
}

interface AddBillingDataSourceFunnelProps {
  onFinish: () => void
  redirectPage?: string
}
export const AddBillingDataSourceFunnel = ({
  onFinish,
  redirectPage,
}: AddBillingDataSourceFunnelProps) => {
  const [activeStep, setActiveStep] = useState(0)

  const { mutateAsync: createDatasource } =
    useCreateBillingDatasourceMutation(redirectPage)

  const form = useForm<BillingDatasourceFormData>({
    validate: (values) => {
      if (activeStep === 0) {
        return zodResolver(
          z.object({
            name: z.string().min(1, "Name is required"),
            type: z.nativeEnum(BillingDatasourceType),
          }),
        )(values)
      }
      if (activeStep === 3) {
        // @ts-expect-error There's a mapping key in the form values for big query
        return zodResolver(bigQueryColumnMappingSchema)(values.mapping)
      }

      return zodResolver(importParametersSchemasMap[values.type!])(values)
    },
  })

  const hasFourSteps =
    form.getValues().type === BillingDatasourceType.CustomBigQuery

  const handleClickNext = () =>
    setActiveStep((current) => {
      if (form.validate().hasErrors) {
        console.log("erros", form.validate().errors)
        return current
      }
      return current < 3 ? current + 1 : current
    })
  const handleClickBack = () =>
    setActiveStep((prev) => (prev > 0 ? prev - 1 : prev))

  const handleValidationSuccess = (validationData: {
    isDetailedBillingTbl?: boolean
  }) => {
    // Update the form with the computed isDetailedBilling field
    const currentValues = form.getValues()

    if (currentValues.type === BillingDatasourceType.GCP) {
      // Only set the field if it hasn't been set yet or if the value is different
      const currentIsDetailedBilling = (
        currentValues as BillingDatasourceFormData & {
          isDetailedBilling?: boolean
        }
      ).isDetailedBilling
      const newIsDetailedBilling = validationData.isDetailedBillingTbl ?? false

      if (currentIsDetailedBilling !== newIsDetailedBilling) {
        form.setFieldValue("isDetailedBilling", newIsDetailedBilling)
      }
    }
  }

  const handleClickImport = async () => {
    console.log("erros", form.validate().errors)
    if (form.validate().hasErrors) {
      return
    }

    try {
      const formValues = form.getValues()

      const { name, type, ...otherFields } = formValues
      const isDetailedBilling = (
        formValues as BillingDatasourceFormData & {
          isDetailedBilling?: boolean
        }
      ).isDetailedBilling

      const payload = {
        name,
        type,
        metadata: {
          ...otherFields,
          ...(isDetailedBilling !== undefined && { isDetailedBilling }),
        },
      }

      await createDatasource(
        payload as BillingDatasourcesRequests.CreateBillingDatasource,
      )

      showNotification({
        title: "Success",
        message: "Datasource has been created",
        color: "green",
      })
      onFinish()
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to create datasource",
        color: "red",
      })
    }
  }

  const { type } = form.getValues()
  const user = useUser()
  const dataProjectId = runtimeConfig.VITE_DATA_PROJECT_ID
  return (
    <Stack h="100%" justify="space-between">
      <Stepper
        active={activeStep}
        onStepClick={setActiveStep}
        allowNextStepsSelect={false}
        h="100%"
        styles={{
          content: {
            height: "95%",
          },
          stepIcon: {
            backgroundColor: "var(--mantine-color-primaryHighlight-0)",
            color: "var(--mantine-color-textOnError-0)",
          },
        }}
      >
        <Stepper.Step label={<Text fw="bold">Select datasource</Text>}>
          <Stack h="100%">
            <Stack flex={1}>
              <Group align="flex-start">
                <InputWrapper
                  label={
                    <Text fw="bold" span={true}>
                      Name
                    </Text>
                  }
                  required
                  w="40%"
                >
                  <TextInput
                    {...form.getInputProps("name")}
                    w="100%"
                    placeholder="Name"
                    data-intercom-target="costory-tour-datasource-billing-name-input"
                  />
                </InputWrapper>
              </Group>
              <InputWrapper
                label={
                  <Text fw="bold" span={true}>
                    Datasource type
                  </Text>
                }
                required
              >
                <Radio.Group
                  {...form.getInputProps("type")}
                  data-intercom-target="costory-tour-datasource-billing-type-select"
                >
                  <Group align="left">
                    {availableDatasources.map((datasource) => {
                      if (datasource.value === BillingDatasourceType.Datadog)
                        return null
                      const isSelected = type === datasource.value
                      const IconComponent = datasource.icon
                      return (
                        <Radio.Card
                          key={datasource.value}
                          value={datasource.value}
                          w={180}
                          h={180}
                          p={8}
                          radius="md"
                          withBorder
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            borderColor: isSelected
                              ? "var(--mantine-color-primaryHighlight-0)"
                              : "var(--mantine-color-border-0)",
                            borderWidth: isSelected ? 2 : 1,
                          }}
                          data-intercom-target={`costory-tour-datasource-billing-${datasource.value.toLowerCase()}-option`}
                        >
                          <Stack
                            justify="center"
                            align="center"
                            w="100%"
                            h="100%"
                          >
                            {IconComponent}
                            <Text fw="bold" ta="center">
                              {datasource.label}
                            </Text>
                          </Stack>
                        </Radio.Card>
                      )
                    })}
                  </Group>
                </Radio.Group>
              </InputWrapper>
            </Stack>
            <Group justify="flex-end">
              <Button
                onClick={handleClickNext}
                rightSection={<IconArrowRight />}
                data-intercom-target="costory-tour-datasource-billing-next-button"
              >
                Next
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label={<Text fw="bold">Import parameters</Text>}>
          <Stack h="100%">
            <Stack flex={1}>
              {type &&
                questions[type]?.map(({ key, label, type, required }) => {
                  // Add BlockQuote for GCP BigQuery Table Path
                  if (
                    type === "text" &&
                    key === "bqTablePath" &&
                    (form.values.type === BillingDatasourceType.GCP ||
                      form.values.type === BillingDatasourceType.CustomBigQuery)
                  ) {
                    return (
                      <Stack key={key} gap="md">
                        <Blockquote
                          color="blue"
                          icon={<IconInfoCircle />}
                          mt="xl"
                        >
                          Share your Bigquery Dataset with tsadei-
                          {user.currentOrg.slug}@{dataProjectId}
                          .iam.gserviceaccount.com:
                        </Blockquote>
                        <InputWrapper
                          label={
                            <Text fw="bold" span={true}>
                              {label}
                            </Text>
                          }
                          required={required}
                        >
                          <TextInput
                            {...form.getInputProps(key)}
                            placeholder={label}
                            type={type}
                            data-intercom-target={`costory-tour-datasource-billing-${key}-input`}
                          />
                        </InputWrapper>
                      </Stack>
                    )
                  }

                  return type === "text" || type === "password" ? (
                    <InputWrapper
                      label={
                        <Text fw="bold" span={true}>
                          {label}
                        </Text>
                      }
                      key={key}
                      required={required}
                    >
                      <TextInput
                        {...form.getInputProps(key)}
                        placeholder={label}
                        type={type}
                        data-intercom-target={`costory-tour-datasource-billing-${key}-input-2`}
                      />
                    </InputWrapper>
                  ) : type === "checkbox" ? (
                    <Checkbox
                      {...form.getInputProps(key, { type: "checkbox" })}
                      label={<Text fw="bold">{label}</Text>}
                      data-intercom-target={`costory-tour-datasource-billing-${key}-checkbox`}
                    />
                  ) : (
                    <InputWrapper
                      label={<Text fw="bold">{label}</Text>}
                      key={key}
                      required={required}
                    >
                      <DateInput
                        {...form.getInputProps(key)}
                        placeholder="01/01/2025"
                      />
                    </InputWrapper>
                  )
                })}
            </Stack>
            <Group justify="space-between">
              <Button
                onClick={handleClickBack}
                variant="outline"
                leftSection={<IconArrowLeft />}
                data-intercom-target="costory-tour-datasource-billing-back-button"
              >
                Back
              </Button>
              <Button
                onClick={handleClickNext}
                rightSection={<IconArrowRight />}
                data-intercom-target="costory-tour-datasource-billing-next-button-2"
              >
                Next
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label={<Text fw="bold">Verification</Text>}>
          <Stack h="100%">
            <VerificationStep
              // @ts-expect-error form values are correct at this point
              importParameters={form.getValues()}
              handleClickBack={handleClickBack}
              handleClickNext={
                hasFourSteps ? handleClickNext : handleClickImport
              }
              isLastStep={!hasFourSteps}
              connectorType={type!}
              onValidationSuccess={handleValidationSuccess}
            />
          </Stack>
        </Stepper.Step>
        {hasFourSteps && (
          <Stepper.Step
            title="Column mapping"
            label={<Text fw="bold">Column mapping</Text>}
          >
            <BigQueryColumnMappingStep
              // @ts-expect-error form values are correct at this point
              form={form}
              handleClickBack={handleClickBack}
              handleClickImport={handleClickImport}
            />
          </Stepper.Step>
        )}
      </Stepper>
    </Stack>
  )
}

interface VerificationStepProps {
  importParameters: BillingDatasourcesRequests.ValidateBillingDatasourceImportParameters
  handleClickBack: () => void
  handleClickNext: () => void
  isLastStep: boolean
  connectorType: BillingDatasourceType
  currentPrefix?: string
  onValidationSuccess?: (validationData: {
    isDetailedBillingTbl?: boolean
  }) => void
}

const VerificationStep = ({
  importParameters,
  handleClickBack,
  handleClickNext,
  isLastStep,
  connectorType,
  currentPrefix,
  onValidationSuccess,
}: VerificationStepProps) => {
  const [isCreating, setIsCreating] = useState(false)
  const [hasShownDetailedBillingModal, setHasShownDetailedBillingModal] =
    useState(false)
  const query = useValidateImportParamtersQuery(importParameters)

  const onClickImport = async () => {
    setIsCreating(true)
    await handleClickNext()
    setIsCreating(false)
  }

  return (
    <Stack h="100%" w="100%">
      <Stack align="center" justify="center" flex={1}>
        <QueryWrapper
          query={query}
          LoadingComponent={
            <Stack align="center">
              <Loader size="xl" />{" "}
              <Title order={2}>Validating datasource...</Title>{" "}
            </Stack>
          }
        >
          {({ data }) => {
            if (data.isSuccess) {
              // Call the callback with validation data for GCP datasources
              if (
                importParameters.type === BillingDatasourceType.GCP &&
                onValidationSuccess
              ) {
                onValidationSuccess({
                  isDetailedBillingTbl: data.isDetailedBillingTbl,
                })
              }

              if (
                importParameters.type === BillingDatasourceType.GCP &&
                data.isDetailedBillingTbl === false &&
                !hasShownDetailedBillingModal
              ) {
                setHasShownDetailedBillingModal(true)
                modals.openConfirmModal({
                  title: "No detailed billing source detected",
                  children: (
                    <Text size="sm">
                      This table is a Standard Billing export. Do you have
                      detailed billing enabled? If yes, please share the
                      detailed billing table path instead: This would allow you
                      to check cost per resource / have access to low level
                      labels.
                    </Text>
                  ),
                  labels: { confirm: "Continue anyway", cancel: "Go back" },
                  onCancel: handleClickBack,
                })
              }

              return (
                <>
                  <ThemeIcon size={120} variant="light" color="success">
                    <IconCheck size={80} />
                  </ThemeIcon>
                  <Title order={2}>Your datasource is valid</Title>
                  <Space h={10} />
                </>
              )
            }

            const bqPath =
              importParameters.type === BillingDatasourceType.CustomBigQuery ||
              importParameters.type === BillingDatasourceType.GCP
                ? importParameters.bqTablePath
                : undefined

            return (
              <>
                <ThemeIcon size={120} variant="light" color="textSecondary">
                  <IconCloudX size={80} />
                </ThemeIcon>
                <Title order={2}>Your datasource is invalid</Title>

                {data.errorCode === "PREFIX" ? (
                  <Stack align="center">
                    <Text size="lg">Prefix parameter is not valid</Text>
                    {data.options.length === 0 ? (
                      <Text>
                        No data found with this prefix. Make sure you&apos;re
                        using the right bucket or try again with an empty
                        prefix.
                      </Text>
                    ) : (
                      <>
                        <Text size="md" c="dimmed" mb={10}>
                          {connectorType === BillingDatasourceType.AWS ? (
                            <>
                              The folder structure should contain both a
                              &quot;data&quot; and &quot;metadata&quot; folder
                              at the root level. For example: &quot;
                              {currentPrefix ?? "my-prefix"}/data/&quot; and
                              &quot;{currentPrefix ?? "my-prefix"}
                              /metadata/&quot;
                            </>
                          ) : (
                            <>
                              The folder structure should include a date range
                              in the format YYYYMMDD-YYYYMMDD after the folder
                              name. For example: &quot;
                              {currentPrefix ?? "my-prefix"}/
                              billing-actual-cost/ 20240101-20240131&quot; for
                              January 2024.
                            </>
                          )}
                        </Text>
                        <Text size="md" fw={500} mb={10}>
                          We found these blobs in your bucket:
                        </Text>
                        <ScrollArea h={100}>
                          <List>
                            {data.options.map((option) => (
                              <ListItem key={option}>
                                <Text size="sm">{option}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </ScrollArea>

                        <Text size="md" c="dimmed" mt={10}>
                          {connectorType === BillingDatasourceType.AWS
                            ? "Please update the prefix to point to a folder containing both data/ and metadata/ folders."
                            : "Please update the prefix to point to a folder containing your billing data with the correct date format."}
                        </Text>
                      </>
                    )}
                  </Stack>
                ) : data.errorCode === "BUCKET" ? (
                  <Text>This bucket does not exist</Text>
                ) : connectorType === BillingDatasourceType.Aiven ? (
                  <Text c="red" ta="center">
                    {data.errorCode === "CREDENTIALS"
                      ? "Invalid Aiven API token. Please check your token and try again."
                      : data.errorCode === "ACCESS_DENIED"
                        ? "Access denied. Please check that your token has the necessary permissions to access the organization's invoices."
                        : data.errorCode === "INVALID_FORMAT"
                          ? "No invoices found for this organization. Please ensure the organization has billing data available."
                          : data.errorCode === "NOT_FOUND"
                            ? "Organization not found. Please check your organization ID and try again."
                            : "Failed to connect to Aiven API. Please check your token and organization ID, then try again."}
                  </Text>
                ) : (
                  <BigQueryErrorDisplay
                    errorCode={data?.errorCode as BQErrorCode}
                    tablePath={bqPath}
                  />
                )}
                <Space h={10} />
                <Group justify="flex-end">
                  <Text>Retry</Text>
                  <ActionIcon
                    loading={query.isLoading}
                    onClick={() =>
                      queryClient.resetQueries({
                        queryKey: ["billingDatasources", "validateImport"],
                      })
                    }
                    data-intercom-target="costory-tour-datasource-billing-refresh-button"
                  >
                    <IconRefresh />
                  </ActionIcon>
                </Group>
              </>
            )
          }}
        </QueryWrapper>
      </Stack>
      <Group justify="space-between">
        <Button
          onClick={handleClickBack}
          variant="outline"
          leftSection={<IconArrowLeft />}
          data-intercom-target="costory-tour-datasource-billing-verification-back-button"
        >
          Back
        </Button>
        {isLastStep ? (
          <Button
            onClick={onClickImport}
            disabled={!query.isSuccess || !query.data.isSuccess}
            loading={isCreating}
            data-intercom-target="costory-tour-datasource-billing-import-button"
          >
            Import
          </Button>
        ) : (
          <Button
            onClick={handleClickNext}
            disabled={!query.isSuccess || !query.data.isSuccess}
            rightSection={<IconArrowRight />}
            data-intercom-target="costory-tour-datasource-billing-verification-next-button"
          >
            Next
          </Button>
        )}
      </Group>
    </Stack>
  )
}

type BigQueryForm = UseFormReturnType<BigQueryBillingDatasourceFormData>
interface BigQueryColumnMappingStepProps {
  form: BigQueryForm
  handleClickBack: () => void
  handleClickImport: () => void
}

const BigQueryColumnMappingStep = ({
  form,
  handleClickBack,
  handleClickImport,
}: BigQueryColumnMappingStepProps) => {
  const query = useGetBigQueryColumnsQuery(form.getValues())

  useEffect(() => {
    const formValues = form.getValues()
    if (!formValues.mapping) {
      form.setValues({
        ...formValues,
        mapping: {
          billedCost: "",
        },
      })
    }
  }, [form])

  return (
    <>
      <QueryWrapper query={query} allowEmptyArray>
        {({ data }) => (
          <BigQueryColumnMappingForm
            {...data}
            form={form}
            handleClickBack={handleClickBack}
            handleClickImport={handleClickImport}
          />
        )}
      </QueryWrapper>
    </>
  )
}

interface BigQueryColumnMappingFormProps
  extends BillingDatasourcesResponses.GetBigQueryMapping {
  handleClickBack: () => void
  handleClickImport: () => void
  form: BigQueryForm
}

const BigQueryColumnMappingForm = ({
  form,
  columnsToMap,
  schema,
  handleClickBack,
  handleClickImport: onImport,
}: BigQueryColumnMappingFormProps) => {
  const [isCreating, setIsCreating] = useState(false)
  const [unmappedFields, setUnmappedFields] = useState<Set<string>>(new Set())

  // Auto-populate mappings when schema loads
  useEffect(() => {
    if (!schema?.fields || !columnsToMap) return

    const currentMapping = (form.values.mapping || {}) as Record<string, string>
    const newMapping: Record<string, string> = { ...currentMapping }
    const newUnmappedFields = new Set<string>()
    let hasChanges = false

    columnsToMap.forEach(({ name, types, required }) => {
      // Only auto-fill if field is not already set
      if (!currentMapping[name]) {
        const eligibleColumns = schema.fields
          .filter((el) => MAPPING_BQ_TYPES[types].includes(el.type))
          .map((field) => field.name)

        const bestMatch = findBestColumnMatch(name, eligibleColumns)
        if (bestMatch) {
          newMapping[name] = bestMatch
          hasChanges = true
        } else if (required) {
          // Track unmapped required fields for highlighting
          newUnmappedFields.add(name)
        }
      }
    })

    if (hasChanges) {
      form.setFieldValue(
        "mapping",
        newMapping as BigQueryBillingDatasourceFormData["mapping"],
      )
    }
    setUnmappedFields(newUnmappedFields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, columnsToMap])

  // Update unmapped fields when form values change
  useEffect(() => {
    const mapping = form.values.mapping
    const newUnmappedFields = new Set<string>()

    columnsToMap.forEach(({ name, required }) => {
      const mappingValue = mapping?.[name as keyof typeof mapping]
      if (required && !mappingValue) {
        newUnmappedFields.add(name)
      }
    })

    setUnmappedFields(newUnmappedFields)
  }, [form.values.mapping, columnsToMap])

  const handleClickImport = async () => {
    if (form.validate().hasErrors) {
      return
    }

    setIsCreating(true)
    await onImport()
    setIsCreating(false)
  }

  useMantineTheme()

  return (
    <Stack h="95%">
      {unmappedFields.size > 0 && (
        <Blockquote color="orange" icon={<IconInfoCircle />}>
          {unmappedFields.size} required field
          {unmappedFields.size > 1 ? "s" : ""} could not be auto-mapped. Please
          map the highlighted fields manually.
        </Blockquote>
      )}
      <Stack flex={1}>
        <table>
          <thead>
            <tr
              style={{
                backgroundColor: "var(--mantine-color-surfaceHeader-0)",
              }}
            >
              <th style={{ padding: 4 }}>Template column</th>
              <th>Column imported from your file</th>
            </tr>
          </thead>
          <tbody>
            {columnsToMap.map(({ label, name, required, types }) => {
              const isUnmapped = unmappedFields.has(name)
              const mapping = form.values.mapping
              const currentValue = mapping?.[name as keyof typeof mapping]

              return (
                <tr
                  key={name}
                  style={{
                    backgroundColor: isUnmapped
                      ? "var(--mantine-color-orange-light)"
                      : undefined,
                  }}
                >
                  <th align="left">
                    {label}
                    {required && (
                      <Text component="span" c="error">
                        {" *"}
                      </Text>
                    )}
                  </th>
                  <td>
                    <Select
                      searchable
                      clearable
                      data={schema?.fields
                        .filter((el) =>
                          MAPPING_BQ_TYPES[types].includes(el.type),
                        )
                        .map((field) => field.name)}
                      {...form.getInputProps(`mapping.${name}`)}
                      placeholder="Select the corresponding column from your datasource"
                      data-intercom-target={`costory-tour-datasource-billing-column-${name}-select`}
                      styles={
                        isUnmapped
                          ? {
                              input: {
                                borderColor: "var(--mantine-color-orange-6)",
                                borderWidth: 2,
                              },
                            }
                          : undefined
                      }
                      rightSection={
                        currentValue ? (
                          <ThemeIcon color="green" size="sm" variant="light">
                            <IconCheck size={14} />
                          </ThemeIcon>
                        ) : undefined
                      }
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Stack>
      <Group justify="space-between">
        <Button
          onClick={handleClickBack}
          variant="outline"
          leftSection={<IconArrowLeft />}
          data-intercom-target="costory-tour-datasource-billing-mapping-back-button"
        >
          Back
        </Button>
        <Button
          onClick={handleClickImport}
          loading={isCreating}
          data-intercom-target="costory-tour-datasource-billing-mapping-import-button"
        >
          Import
        </Button>
      </Group>
    </Stack>
  )
}

const availableDatasources = [
  {
    label: "AWS",
    value: BillingDatasourceType.AWS,
    icon: <AwsSvgComponent />,
  },
  {
    label: "Azure",
    value: BillingDatasourceType.Azure,
    icon: <AzureSvgComponent />,
  },
  {
    label: "GCP",
    value: BillingDatasourceType.GCP,
    icon: <GcpSvgComponent />,
  },
  {
    label: "Datadog",
    value: BillingDatasourceType.Datadog,
    icon: <DatadogSvgComponent />,
  },
  {
    label: "Confluent",
    value: BillingDatasourceType.CONFLUENT,
    icon: <ConfluentSvgComponent />,
  },
  {
    label: "Custom Billing Format (FOCUS) (Big Query Table)",
    value: BillingDatasourceType.CustomBigQuery,
    icon: <BigquerySvgComponent />,
  },
  {
    label: "Aiven",
    value: BillingDatasourceType.Aiven,
    icon: <AivenSvgComponent />,
  },
]

const questions: Record<
  BillingDatasourceType,
  {
    label: string
    key:
      | KeysOfUnion<BillingDatasourceFormData["metadata"]>
      | KeysOfUnion<ConfluentConfigurationFormData>
    type: "text" | "date" | "checkbox" | "password"
    required: boolean
  }[]
> = {
  [BillingDatasourceType.AWS]: [
    {
      label: "Bucket Name",
      key: "bucketName",
      type: "text",
      required: true,
    },
    {
      label: "Role ARN",
      key: "roleArn",
      type: "text",
      required: true,
    },
    {
      label: "Prefix",
      key: "prefix",
      type: "text",
      required: false,
    },
    {
      label: "EKS Split cost data enabled",
      key: "eksSplitDataEnabled",
      type: "checkbox",
      required: true,
    },
    {
      label: "Start Date",
      key: "startDate",
      type: "date",
      required: false,
    },
    {
      label: "End Date",
      key: "endDate",
      type: "date",
      required: false,
    },
  ],
  [BillingDatasourceType.GCP]: [
    {
      label: "Big Query Table Path",
      key: "bqTablePath",
      type: "text",
      required: true,
    },
    {
      label: "Start Date",
      key: "startDate",
      type: "date",
      required: false,
    },
    {
      label: "End Date",
      key: "endDate",
      type: "date",
      required: false,
    },
  ],
  [BillingDatasourceType.Azure]: [
    {
      label: "SAS Token",
      key: "sasToken",
      type: "text",
      required: true,
    },
    {
      label: "Container Name",
      key: "containerName",
      type: "text",
      required: true,
    },
    {
      label: "Account",
      key: "account",
      type: "text",
      required: true,
    },
    {
      label: "Prefix",
      key: "prefix",
      type: "text",
      required: false,
    },
  ],
  [BillingDatasourceType.CustomBigQuery]: [
    {
      label: "BigQuery Table Path",
      key: "bqTablePath",
      type: "text",
      required: true,
    },
    {
      label: "Provider Name",
      key: "providerName",
      type: "text",
      required: true,
    },
    {
      label: "Billing Account Id",
      key: "billingAccountId",
      type: "text",
      required: false,
    },
  ],
  [BillingDatasourceType.CONFLUENT]: [
    {
      label: "API Key",
      key: "apiKey",
      type: "password",
      required: true,
    },
    {
      label: "API Secret",
      key: "apiSecret",
      type: "password",
      required: true,
    },
  ],
  [BillingDatasourceType.Datadog]: [],
  [BillingDatasourceType.Aiven]: [
    {
      label: "Aiven Token",
      key: "apiSecret",
      type: "password",
      required: true,
    },
    {
      label: "Organization ID",
      key: "organizationId",
      type: "text",
      required: true,
    },
  ],
}

const importParametersSchemasMap = {
  [BillingDatasourceType.AWS]: awsImportParametersSchema,
  [BillingDatasourceType.GCP]: gcpImportParametersSchema,
  [BillingDatasourceType.Azure]: azureImportParametersSchema,
  [BillingDatasourceType.CustomBigQuery]: bigQueryImportParametersSchema,
  [BillingDatasourceType.CONFLUENT]: confluentConfigurationSchema,
  [BillingDatasourceType.Datadog]: datadogMetadataSchema,
  [BillingDatasourceType.Aiven]: aivenMetadataSchema,
}
